import { useEffect, useMemo, useState } from 'react'

import { type PimProductWithVariantsV1 } from '@redteclab/api/clients/bully'

import { type AttributeGroupData, type AttributeGroups } from './AttributeGroup'

const useGetNonSelectedGroups = (
  selectionGroupsMap: AttributeGroups,
): AttributeGroups => {
  const unselectedGroups = useMemo(() => {
    return Object.values(selectionGroupsMap).filter((it) => {
      if (!it?.shouldRenderGroup) {
        return false
      }

      return it.selectionEntries.every((entry) => !entry.isSelected)
    })
  }, [selectionGroupsMap])

  const result: AttributeGroups = {}
  for (const group of unselectedGroups) {
    if (group) {
      result[group.metaAttributeData.variant_attribute] = group
    }
  }

  return result
}
export type NotSelectedValidationErrorControls = {
  getShouldShowAttributeMustBeSelectedErrorForGroup: (
    group: AttributeGroupData,
  ) => boolean
  hasUnselectedAttributes: boolean
  noAttributeSelectedError: boolean
  validateWhenAddingToCart: () => boolean
}

/**
 * Hook controls showing a validation errors when user did not select some attributes,
 * and tried to put a product in a cart.
 */
export const useNotSelectedValidationError = ({
  product,
  selectionGroupsMap,
}: {
  product: PimProductWithVariantsV1
  selectionGroupsMap: AttributeGroups
}): NotSelectedValidationErrorControls => {
  /**
   * after user tries to submit, in case he has not selected some attirbute we need to show errors.
   */
  const [showError, setShowError] = useState<boolean>(false)

  const notSelectedGroups = useGetNonSelectedGroups(selectionGroupsMap)

  /**
   * in case product is switched, errors shall be hidden until user tries to submit again.
   */
  useEffect(() => {
    // user selected all - unset error
    if (showError && Object.values(notSelectedGroups).length === 0) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- don't reference, refactor if touched
      setShowError(false)
    }
  }, [showError, product, notSelectedGroups])

  const getShouldShowAttributeMustBeSelectedErrorForGroup = (
    group: AttributeGroupData,
  ): boolean => {
    if (!showError) {
      return false
    }

    return Boolean(notSelectedGroups[group.metaAttributeData.variant_attribute])
  }
  const hasUnselectedAttributes = Object.keys(notSelectedGroups).length > 0

  const noAttributeSelectedError = Object.values(selectionGroupsMap).some(
    (selectionGroup) =>
      selectionGroup !== undefined &&
      getShouldShowAttributeMustBeSelectedErrorForGroup(selectionGroup),
  )

  return {
    getShouldShowAttributeMustBeSelectedErrorForGroup,
    hasUnselectedAttributes,
    noAttributeSelectedError,
    validateWhenAddingToCart: (): boolean => {
      if (hasUnselectedAttributes) {
        setShowError(true)

        return false
      }

      return true
    },
  }
}
